import React from "react";
import { StyledErrorHandler } from "@components/ErrorHandler/ErrorHandler.styles";
import type { ErrorHandlerProps } from "@components/ErrorHandler/ErrorHandler.types";
import { useTranslation } from "react-i18next";

const ErrorHandler: React.FC<ErrorHandlerProps> = ({ error, testId = "error-handler" }) => {
  const { t } = useTranslation();

  const getStatusCode = (error: any): number | null => {
    // Check error.response.status first (standard Axios error format)
    if (error?.response?.status) {
      return error.response.status;
    }
    if (error?.response?.data?.status) {
      return error.response.data.status;
    }
    if (error?.status) {
      return error.status;
    }
    return null;
  };

  const getBackendMessage = (error: any): string | null => {
    // Try to extract the backend error message
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }
    return null;
  };

  const getErrorContent = (statusCode: number | null): { title: string; message: string } => {
    switch (statusCode) {
      case 400:
        return {
          title: t("error.bad-request.title"),
          message: t("error.bad-request.message"),
        };
      case 401:
        return {
          title: t("error.unauthorized.title"),
          message: t("error.unauthorized.message"),
        };
      case 403:
        return {
          title: t("error.forbidden.title"),
          message: t("error.forbidden.message"),
        };
      case 404:
        return {
          title: t("error.not-found.title"),
          message: t("error.not-found.message"),
        };
      case 408:
        return {
          title: t("error.timeout.title"),
          message: t("error.timeout.message"),
        };
      case 429:
        return {
          title: t("error.too-many-requests.title"),
          message: t("error.too-many-requests.message"),
        };
      case 500:
        return {
          title: t("error.internal-server-error.title"),
          message: t("error.internal-server-error.message"),
        };
      case 502:
      case 503:
      case 504:
        return {
          title: t("error.service-unavailable.title"),
          message: t("error.service-unavailable.message"),
        };
      default:
        return {
          title: t("error.generic.title"),
          message: t("error.generic.message"),
        };
    }
  };

  const statusCode = getStatusCode(error);
  const { title, message: defaultMessage } = getErrorContent(statusCode);
  const message = getBackendMessage(error) ?? defaultMessage;

  return (
    <StyledErrorHandler className="p-8" data-testid={testId}>
      <div className="font-bold mb-2">{title}</div>
      <div>{message}</div>
    </StyledErrorHandler>
  );
};

export default ErrorHandler;
